import { Dispatch, SetStateAction, useMemo, useRef, useState } from 'react'
import { createIntradayHourLabel } from 'views/IntradayTrading/intradayTime.helpers'
import { isValidPriceValue } from '../../IntradayTradingView.helpers'
import type { HourInput } from '../../IntradayTradingView.types'

const DEFAULT_INTRADAY_PRICE_ROWS = 12

function createPriceInput(id: number): HourInput {
  return {
    id,
    value: '',
  }
}

function createInitialPriceInputs(): HourInput[] {
  return Array.from({ length: DEFAULT_INTRADAY_PRICE_ROWS }, (_unused, index) => createPriceInput(index))
}

type UsePriceInputRowsResult = {
  inputPrices: HourInput[]
  setInputPrices: Dispatch<SetStateAction<HourInput[]>>
  hasInvalidPriceInputs: boolean
  priceHourLabels: string[]
  handlePriceInputChange: (id: number, value: string) => void
  handleAddPriceRow: () => void
  handleRemoveLastPriceRow: () => void
}

export function usePriceInputRows(): UsePriceInputRowsResult {
  const [inputPrices, setInputPrices] = useState<HourInput[]>(() => createInitialPriceInputs())
  const nextPriceInputId = useRef(DEFAULT_INTRADAY_PRICE_ROWS)

  const hasInvalidPriceInputs = useMemo(
    () => inputPrices.some((price) => price.value.trim() !== '' && !isValidPriceValue(price.value)),
    [inputPrices]
  )
  const priceHourLabels = useMemo(() => inputPrices.map((_row, index) => createIntradayHourLabel(index)), [inputPrices])

  function handlePriceInputChange(id: number, value: string): void {
    setInputPrices((previous) => previous.map((item) => (item.id === id ? { ...item, value } : item)))
  }

  function handleAddPriceRow(): void {
    setInputPrices((previous) => [...previous, createPriceInput(nextPriceInputId.current++)])
  }

  function handleRemovePriceRow(id: number): void {
    setInputPrices((previous) => previous.filter((item) => item.id !== id))
  }

  function handleRemoveLastPriceRow(): void {
    const lastPriceRow = inputPrices[inputPrices.length - 1]
    if (!lastPriceRow) return
    handleRemovePriceRow(lastPriceRow.id)
  }

  return {
    inputPrices,
    setInputPrices,
    hasInvalidPriceInputs,
    priceHourLabels,
    handlePriceInputChange,
    handleAddPriceRow,
    handleRemoveLastPriceRow,
  }
}
